import { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import { CustomContainer } from "components/atoms/DependencyHelper";
import LabelText from "components/atoms/label/LabelText";

import {
  imagePrefix,
} from "utils/helperServices";
import { useScreenType } from "utils/hooks/common";

import CrossIcon from './CrossIcon.svg';
import styles from "./ColorSwatch.module.scss"

import {
  NotificationTypes,
  ScreenSizeTypes,
} from "../../../config/constants";

const getNoticeConfigs = (noticeType) => {
  const configs = {
    iconUrl: "",
    labelClass: "",
    indicatorClass: "",
  };

  switch (noticeType) {
    case NotificationTypes.Error:
      configs.iconUrl = `${imagePrefix}/assets/images/error-warning-red.svg`;
      configs.indicatorClass = styles["swatch-color-indicator--notice--error"];
      configs.labelClass = styles["swatch-notification-error"];
      break;
    case NotificationTypes.Warning:
      configs.iconUrl = `${imagePrefix}/assets/images/error-warning-orange.svg`;
      configs.indicatorClass = styles["swatch-color-indicator--notice--warning"];
      configs.labelClass = styles["swatch-notification-warning"];
      break;
    case NotificationTypes.Success:
    default:
      configs.iconUrl = "";
      configs.labelClass = "";
      configs.indicatorClass = "";
      break;
  }

  return configs;
};

export const ColorSwatchModes = {
  Pill: "pill",
  Rounded: "rounded",
};

export const NotificationPositions = {
  Bottom: "bottom",
  Right: "right",
}

const SwatchColor = styled.span`
  background-color: ${props => props.color};
`;

const ColorSwatch = (props) => {
  const notice = props.selectedOption?.notification?.text || "";
  const noticeType = props.selectedOption?.notification?.type;

  const [showToolTip, setShowTooltip] = useState(false);

  const isDesktop = useScreenType() === ScreenSizeTypes.Desktop;
  const { iconUrl, indicatorClass, labelClass } = getNoticeConfigs(noticeType);

  const handleColorSelect = (item) => {
    if (
      props.disabled
      || item.id === props.selectedOption?.id
    ) return;

    typeof props.onColorSelect === "function"
      && props.onColorSelect(item);
  }

  if (Array.isArray(props.options)
    && props.options.length === 1
    && props.options[0].id !== props.selectedOption?.id
  ) {
    handleColorSelect(props.options[0]);
  }

  let modeClassName;

  switch(props.swatchMode) {
    case ColorSwatchModes.Pill:
      modeClassName = "swatch-color-indicator-pill";
      break;
    case ColorSwatchModes.Rounded:
    default:
      modeClassName = "swatch-color-indicator-rounded";
      break;
  }

  const renderSwatchColor = (swatchItem) => {
    const isSelected = swatchItem.id === props.selectedOption?.id;

    return (
      <SwatchColor
        className={`
          ${styles["swatch-color-indicator"]} 
          ${styles[modeClassName]} 
          ${isSelected ? styles["swatch-color-indicator--selected"] : ""}
          ${notice ? indicatorClass : ""}
        `}
        color={swatchItem.value}
        key={swatchItem.id}
        onClick={() => handleColorSelect(swatchItem)}
      >
        { !swatchItem.isAvailable && <CrossIcon className={styles["swatch-color-icon"]} /> }
      </SwatchColor>
    );
  }

  const renderSwatchNotice = () => {
    if (!notice) return null;

    return (
      <OverlayTrigger
        placement="bottom-start"
        overlay={notificationOverlay}
        delay={{ hide: 1000, show: 500 }}
        defaultShow={false}
        onToggle={setShowTooltip}
        show={showToolTip}
      >
        <CustomContainer
          className={styles["swatch-notification"]}
          onClick={props.toggleNotice}
        >
          <img
            className={styles["swatch-notification-icon"]}
            src={iconUrl}
          />
          <span className={`${styles["swatch-notification-label"]} ${labelClass}`}>{ notice }</span>
        </CustomContainer>
      </OverlayTrigger>
    );
  }

  if (!Array.isArray(props.options) || props.options.length === 0) return null;

  const notificationOverlay = isDesktop && props.selectedOption?.notification?.description
    ? <Tooltip id={"tooltip-notification"}>{ props.selectedOption?.notification?.description }</Tooltip>
    : <span />;

  return (
    <CustomContainer className={`${styles["color-swatch-container"]} ${props.className}`}>
      <LabelText className={styles["swatch-color-title"]}>
        {props.title}: 
        <span className={styles["swatch-color-label"]}> {props.selectedOption?.label || ""}</span>
      </LabelText>

      <CustomContainer
        className={`${styles["color-swatch-content"]} 
          ${props.alignNotification === NotificationPositions.Bottom
            ? styles["color-swatch-content--bottom"]
            : styles["color-swatch-content--right"]
          }`
        }
      >
        <CustomContainer className={styles["color-swatch-group"]}>
          { Array.isArray(props.options) && props.options.map(renderSwatchColor)}
        </CustomContainer>

        { renderSwatchNotice() }
      </CustomContainer>
    </CustomContainer>
  );
}

const ColorSwatchOption = PropTypes.shape({
  label: PropTypes.string.isRequired,
  id: PropTypes.number,
  isAvailable: PropTypes.bool,
  notification: PropTypes.shape({
    text: PropTypes.string.isRequired,
    description: PropTypes.string,
    type: PropTypes.oneOf([
      NotificationTypes.Error,
      NotificationTypes.Success,
      NotificationTypes.Warning
    ])
  }),
  value: PropTypes.string.isRequired,
});

ColorSwatch.propTypes = {
  alignNotification: PropTypes.oneOf([NotificationPositions.Bottom, NotificationPositions.Right]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onColorSelect: PropTypes.func,
  options: PropTypes.arrayOf(ColorSwatchOption).isRequired,
  selectedOption: ColorSwatchOption,
  swatchMode: PropTypes.oneOf([ColorSwatchModes.Pill, ColorSwatchModes.Rounded]),
  title: PropTypes.string,
  toggleNotice: PropTypes.func,
};

ColorSwatch.defaultProps = {
  alignNotification: NotificationPositions.Bottom,
  className: "",
  disabled: false,
  options: [],
  selectedOption: null,
  swatchMode: ColorSwatchModes.Rounded,
  title: "Choose a colour",
  toggleNotice: () => null
};

export default ColorSwatch;
